/**
 * Shared invoice display helpers used by both InvoicingView and the
 * invoiceConsolidation utility.
 *
 * Extracting here avoids a component -> lib dependency inversion.
 */

import { buildInvoicePricingSummary } from '../components/invoicing/invoiceRateResolution';
import { resolveWaitTimeCharge } from '../components/invoicing/waitTimeGraceMatrix';

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const COMPONENT_AMOUNT_KEYS = ['amount', 'value', 'total', 'fee', 'discount', 'surcharge', 'tax'];

function toNumber(value) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
}

function readComponentAmount(value) {
        if (Array.isArray(value)) {
                    return roundMoney(value.reduce((sum, entry) => sum + readComponentAmount(entry), 0));
        }

        if (value && typeof value === 'object') {
                    for (const key of COMPONENT_AMOUNT_KEYS) {
                                if (value[key] !== undefined && value[key] !== null) {
                                            return roundMoney(toNumber(value[key]));
                                }
                    }
                    return roundMoney(
                                Object.values(value).reduce((sum, entry) => {
                                            if (typeof entry === 'number') return sum + entry;
                                            if (Array.isArray(entry)) return sum + readComponentAmount(entry);
                                            if (entry && typeof entry === 'object') return sum + readComponentAmount(entry);
                                            return sum;
                                }, 0)
                    );
        }

        return roundMoney(toNumber(value));
}

/**
 * Resolve the generated line-item pricing summary for an invoice, when
 * the invoice carries line_items (negotiated-rate / tier-discount aware,
 * from Gate 7's rate resolution). Returns null when there are no
 * line_items to price from.
 */
function getInvoicePricingSummary(invoice) {
        if (!Array.isArray(invoice?.line_items) || invoice.line_items.length === 0) return null;
        return buildInvoicePricingSummary({
                    lineItems: invoice.line_items,
                    tierDiscountPct: invoice?.tier_base_discount_pct ?? invoice?.base_discount_pct ?? 0,
                    negotiatedRateSchedule: invoice?.negotiated_rate_schedule ?? null,
                    discountResolutionMode: invoice?.discount_resolution_mode ?? 'override'
        });
}

function getGeneratedWaitTimeCharge(invoice) {
        // Canonical payload is wait_time_grace_rules (app resolver input).
        // wait_time_grace_matrix is accepted as a compatibility fallback.
        const rules = Array.isArray(invoice?.wait_time_grace_rules)
            ? invoice.wait_time_grace_rules
            : (Array.isArray(invoice?.wait_time_grace_matrix) ? invoice.wait_time_grace_matrix : []);

        if (rules.length === 0) return 0;

        const waitTimeContext = invoice?.wait_time_context || {};
        const { waitCharge } = resolveWaitTimeCharge({
            rules,
            context: {
                pickupLocationType: waitTimeContext.pickupLocationType ?? invoice?.pickup_location_type,
                vipStatus: waitTimeContext.vipStatus ?? invoice?.vip_status,
                stopPhase: waitTimeContext.stopPhase ?? invoice?.stop_phase
            },
            waitMinutes: waitTimeContext.waitMinutes
                ?? invoice?.wait_time_minutes
                ?? invoice?.wait_minutes
                ?? 0,
            ratePerMinute: waitTimeContext.ratePerMinute
                ?? invoice?.wait_time_rate_per_minute
                ?? invoice?.wait_rate_per_minute
                ?? 0
        });

        return waitCharge;
}

/**
 * Return the canonical display amount for an invoice row.
 *
 * Priority: final_amount (Gate 1 structured field) -> generated line-item
 * pricing (Gate 7 negotiated-rate/tier-discount resolution) -> total
 * (real DB column, legacy fallback).
 *
 * @param {object|null} invoice
 * @returns {number}
 */
export function getInvoiceDisplayAmount(invoice) {
        const finalAmount = invoice?.final_amount;
        if (!(finalAmount === '' || finalAmount === null || finalAmount === undefined)) {
                    return parseFloat(finalAmount ?? 0) || 0;
        }

        const assemblySummary = buildInvoiceAssemblySummary(invoice);
        if (assemblySummary.isDeterministic) {
                    return assemblySummary.total;
        }

    const generatedPricing = getInvoicePricingSummary(invoice);
        if (generatedPricing) {
                    return generatedPricing.total + getGeneratedWaitTimeCharge(invoice);
        }

    const amount = invoice?.total;

    return parseFloat(amount ?? 0) || 0;
}

export function buildInvoiceAssemblySummary(invoice) {
        const generatedPricing = getInvoicePricingSummary(invoice);

        const lineItemFares = generatedPricing?.subtotal ?? 0;
        const lineItemDiscounts = generatedPricing?.totalDiscount ?? 0;

        const explicitFares = readComponentAmount(invoice?.fares ?? invoice?.fare_total ?? invoice?.fares_total);
        const fees = readComponentAmount(invoice?.fees ?? invoice?.fee ?? invoice?.fees_total ?? invoice?.additional_fees);
        const explicitSurcharges = readComponentAmount(invoice?.surcharges ?? invoice?.surcharge ?? invoice?.surcharge_total);
        const explicitDiscounts = readComponentAmount(invoice?.discounts ?? invoice?.discount ?? invoice?.discount_total);
        const waitTimeSurcharge = generatedPricing ? getGeneratedWaitTimeCharge(invoice) : 0;

        const fares = roundMoney(generatedPricing ? lineItemFares : explicitFares);
        const surcharges = roundMoney(explicitSurcharges + waitTimeSurcharge);
        const discounts = roundMoney(lineItemDiscounts + explicitDiscounts);

        const beforeTaxAndCard = roundMoney(fares + fees + surcharges - discounts);

        const explicitTax = readComponentAmount(invoice?.tax ?? invoice?.tax_amount);
        const taxRate = toNumber(invoice?.tax_rate ?? invoice?.tax_percent ?? invoice?.tax_percentage);
        const tax = explicitTax > 0 ? explicitTax : roundMoney(beforeTaxAndCard * (taxRate / 100));

        const taxInclusiveSubtotal = roundMoney(beforeTaxAndCard + tax);
        const explicitCardFee = readComponentAmount(invoice?.card_fee ?? invoice?.card_fee_amount ?? invoice?.convenience_fee);
        const cardFeePercent = toNumber(invoice?.card_fee_percent ?? invoice?.convenience_fee_percent);
        const cardFee = explicitCardFee > 0 ? explicitCardFee : roundMoney(taxInclusiveSubtotal * (cardFeePercent / 100));

        const total = roundMoney(taxInclusiveSubtotal + cardFee);

        const hasAssemblyInputs =
            Boolean(generatedPricing)
            || fares > 0
            || fees > 0
            || surcharges > 0
            || discounts > 0
            || explicitTax > 0
            || taxRate > 0
            || explicitCardFee > 0
            || cardFeePercent > 0;

        return {
                    fares,
                    fees,
                    surcharges,
                    discounts,
                    tax,
                    cardFee,
                    total,
                    isDeterministic: hasAssemblyInputs
        };
}

/**
 * Return the itemized negotiated-rate / tier-discount lines for an
 * invoice, for display alongside its amount. Empty array when the
 * invoice has no generated line-item pricing.
 *
 * @param {object|null} invoice
 * @returns {Array<{code: string, label: string, amount: number}>}
 */
export function getInvoiceDiscountLines(invoice) {
        return getInvoicePricingSummary(invoice)?.discountLines || [];
}
